import React, { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { useParams, useNavigate } from "react-router-dom";
import { Send, Bot, User, ArrowLeft, ThumbsUp, ThumbsDown, Loader2 } from "lucide-react";
import api from "./api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { ScrollArea } from "../ui/scroll-area";
import { cn } from "../../lib/utils";
import FeedbackBar from "../FeedbackBar/FeedbackBar";

// Thinking phrases for the loading indicator
const THINKING_PHRASES = [
    "Processing data",
    "Analyzing results",
    "Finding information",
    "Checking database",
    "Computing response",
    "Searching records",
    "Reviewing data",
    "Gathering information",
    "Processing request",
    "Checking sources"
];

const ConversationPage = ({ resetActiveConversation }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    
    const [chats, setChats] = useState([]);
    const [formData, setFormData] = useState({ message: "" });
    const [isTyping, setIsTyping] = useState(false);
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const [thinkingPhrase, setThinkingPhrase] = useState("Thinking");
    const [feedbackState, setFeedbackState] = useState({});

    // Effect for cycling through thinking phrases
    useEffect(() => {
        if (!isTyping) return;
        setThinkingPhrase("Thinking");
        
        const interval = setInterval(() => {
            const randomIndex = Math.floor(Math.random() * THINKING_PHRASES.length);
            setThinkingPhrase(THINKING_PHRASES[randomIndex]);
        }, 3000);

        return () => clearInterval(interval);
    }, [isTyping]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    // Fetch conversation messages
    useEffect(() => {
        const fetchMessages = async () => {
            if (id) {
                try {
                    const response = await api.get(`/conversations/${id}`);
                    if (response.data && response.data.messages) {
                        setChats(response.data.messages);
                    }
                } catch (error) {
                    console.error("Error fetching conversation:", error);
                }
            }
        };
        fetchMessages();
    }, [id]);

    useEffect(() => {
        scrollToBottom();
    }, [chats, isTyping]);

    useEffect(() => {
        if (!isFeedbackOpen) {
            setSelectedFeedback(null);
        }
    }, [isFeedbackOpen]);

    const handleInputChange = (event) => {
        setFormData({ ...formData, [event.target.name]: event.target.value });
    };

    const handleFormSubmit = async (event) => {
        event.preventDefault();
        if (formData.message.trim()) {
            setIsTyping(true);
            try {
                await api.post(`/invoke`, {
                    message: formData.message,
                    id: parseInt(id, 10),
                });
                const response = await api.get(`/conversations/${id}`);
                setChats(response.data.messages);
                setFormData({ message: "" });
            } catch (error) {
                console.error("Error sending message:", error);
            }
            setIsTyping(false);
        }
    };

    const handleBackToNewConversation = () => {
        resetActiveConversation();
        navigate("/");
    };

    const handleFeedback = (message, rating, index) => {
        setFeedbackState(prev => ({ ...prev, [index]: rating }));
        setSelectedFeedback({
            message: message.message,
            sender: message.sender,
            rating: rating,
            index: index
        });
        setIsFeedbackOpen(true);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleFormSubmit(e);
        }
    };

    return (
        <div className="flex flex-col h-full bg-gradient-to-b from-background to-muted/20">
            {/* Header */}
            <div className="flex-none px-6 py-4 border-b bg-background/80 backdrop-blur-sm">
                <div className="max-w-4xl mx-auto">
                    <div className="flex items-center gap-3">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleBackToNewConversation}
                            className="mr-2"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div className="p-2 rounded-xl bg-primary/10">
                            <Bot className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                            <h1 className="text-xl font-semibold tracking-tight">Conversation #{id}</h1>
                            <p className="text-sm text-muted-foreground">Chat History</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Messages Area */}
            <ScrollArea className="flex-1 px-4 py-6">
                <div className="max-w-4xl mx-auto space-y-6">
                    {chats.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 space-y-4">
                            <div className="p-4 rounded-2xl bg-muted">
                                <Bot className="h-12 w-12 text-muted-foreground" />
                            </div>
                            <p className="text-muted-foreground">No messages in this conversation yet.</p>
                        </div>
                    ) : (
                        <>
                            {chats.map((chat, index) => (
                                <div
                                    key={index}
                                    className={cn(
                                        "flex gap-3 animate-fade-in",
                                        chat.sender === "user" ? "flex-row-reverse" : "flex-row"
                                    )}
                                >
                                    {/* Avatar */}
                                    <Avatar className={cn(
                                        "h-8 w-8 flex-shrink-0",
                                        chat.sender === "user" ? "bg-primary" : "bg-muted"
                                    )}>
                                        <AvatarFallback className={chat.sender === "user" ? "bg-primary text-primary-foreground" : "bg-muted"}>
                                            {chat.sender === "user" ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                                        </AvatarFallback>
                                    </Avatar>

                                    {/* Message Bubble */}
                                    <div className={cn(
                                        "flex flex-col gap-2 max-w-[80%]",
                                        chat.sender === "user" ? "items-end" : "items-start"
                                    )}>
                                        <div className={cn(
                                            "px-4 py-3 rounded-2xl",
                                            chat.sender === "user" 
                                                ? "bg-primary text-primary-foreground rounded-tr-md" 
                                                : "bg-card border shadow-sm rounded-tl-md"
                                        )}>
                                            {chat.sender === "bot" ? (
                                                <div className="prose prose-sm dark:prose-invert max-w-none">
                                                    <ReactMarkdown>{String(chat.message || '')}</ReactMarkdown>
                                                </div>
                                            ) : (
                                                <p className="text-sm">{chat.message}</p>
                                            )}
                                        </div>

                                        {/* Feedback Buttons for Bot Messages */}
                                        {chat.sender === "bot" && (
                                            <div className="flex items-center gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className={cn(
                                                        "h-7 px-2 text-xs",
                                                        feedbackState[index] === 5 && "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                                    )}
                                                    onClick={() => handleFeedback(chat, 5, index)}
                                                >
                                                    <ThumbsUp className="h-3.5 w-3.5 mr-1" />
                                                    Good
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className={cn(
                                                        "h-7 px-2 text-xs",
                                                        feedbackState[index] === 1 && "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                                                    )}
                                                    onClick={() => handleFeedback(chat, 1, index)}
                                                >
                                                    <ThumbsDown className="h-3.5 w-3.5 mr-1" />
                                                    Bad
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}

                            {/* Typing Indicator */}
                            {isTyping && (
                                <div className="flex gap-3 animate-fade-in">
                                    <Avatar className="h-8 w-8 bg-muted">
                                        <AvatarFallback className="bg-muted">
                                            <Bot className="h-4 w-4" />
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="px-4 py-3 rounded-2xl rounded-tl-md bg-card border shadow-sm">
                                        <div className="flex items-center gap-2">
                                            <Loader2 className="h-4 w-4 animate-spin text-primary" />
                                            <span className="text-sm text-muted-foreground">{thinkingPhrase}</span>
                                            <span className="flex gap-0.5">
                                                <span className="w-1.5 h-1.5 bg-muted-foreground/50 rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
                                                <span className="w-1.5 h-1.5 bg-muted-foreground/50 rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
                                                <span className="w-1.5 h-1.5 bg-muted-foreground/50 rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    <div ref={messagesEndRef} />
                </div>
            </ScrollArea>

            {/* Input Area */}
            <div className="flex-none px-4 py-4 border-t bg-background">
                <form onSubmit={handleFormSubmit} className="max-w-4xl mx-auto">
                    <div className="flex gap-2">
                        <Input
                            ref={inputRef}
                            type="text"
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            placeholder="Continue the conversation..."
                            className="flex-1 h-12 px-4 text-base rounded-xl"
                            disabled={isTyping}
                        />
                        <Button 
                            type="submit" 
                            size="lg"
                            className="h-12 px-6 rounded-xl"
                            disabled={isTyping || !formData.message.trim()}
                        >
                            <Send className="h-5 w-5" />
                            <span className="sr-only">Send message</span>
                        </Button>
                    </div>
                </form>
            </div>

            {/* Feedback Bar */}
            <FeedbackBar
                conversationId={id}
                isOpen={isFeedbackOpen}
                onToggle={() => setIsFeedbackOpen(!isFeedbackOpen)}
                messages={chats}
                preSelectedMessage={selectedFeedback}
                initialRating={selectedFeedback?.rating}
                selectedMessageIndex={selectedFeedback?.index}
            />
        </div>
    );
};

export default ConversationPage;
